// https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events

import { Vector } from "apache-arrow";
import { Vector2 } from "three";
import { CameraManager } from "../../camera/CameraManager";
import { ControlManager } from "../../camera/controls/ControlManager";
import { TGenericVoidFunction } from "../camera-utils";
import { IInputEventHandler, InputPointerType, InputEventType, InputMultiPointerEvent, IScreenPoint, SupportedKeyboardKey } from "./interface";
import { ScreenPoint } from "./screen-point";
import { Viewer } from "../../viewer";


const clickTolerance = 10;//px
const doubleClickTimeDuration = 200;

interface IPointerInfo { //pointerId represents the device not the buttons
    pointerdownPoint: IScreenPoint;
    pointermovePoint: IScreenPoint;
    pointerdownCount: number;
    isPointerUp: boolean;
    isPointerDown: boolean;
    isDoubleClicked: boolean;
    pointerDownAnalyzer?: any;
    pointerUpAnalyzer?: any;
    isTimerNeeded: boolean;
    pointerEvent: PointerEvent;
}


//multiple mouse buttons cannot be clicked at a same moment //pointer down and pointer up is not detectable
//one button per device is permissable

export interface IInputHandlerConfig {
    useSetPointerCapture: boolean;
}

const debounce = (func: any, waitInMS: number) => {
    let timeout: any;

    return function executedFunction(...args: any[]) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, waitInMS);
    };
};

// https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events
export class InputHandler {
    private pinchRadius: number;
    private pinchCenter: IScreenPoint;
    private pointerMap: Map<number, IPointerInfo>;
    private isPointerMoved: boolean;
    private isPointerDragged: boolean;
    private _inputHandlers: Map<string, IInputEventHandler>;
    private _supportedKeyMap: Map<SupportedKeyboardKey, boolean>;

    private _element: HTMLElement;
    private _useSetPointerCapture: boolean;
    private _controlManager: ControlManager;

    private _viewer: Viewer // ToDo test codeのため消す

    public setControlManager(ctrMgr: ControlManager) {
        this._controlManager = ctrMgr;
        this._inputHandlers.set("control", this._controlManager)
    }

    public get supportedKeyMap() {
        return this._supportedKeyMap;
    }

    constructor(viewer: Viewer, element: HTMLElement, config?: IInputHandlerConfig) {
        this._element = element;
        this._useSetPointerCapture = config?.useSetPointerCapture ?? true;
        this._inputHandlers = new Map();
        this.isPointerMoved = false;
        this.isPointerDragged = false;
        this.pointerMap = new Map();
        this.pinchRadius = 0;
        this.pinchCenter = new ScreenPoint();
        this.addEventListeners();
        this._supportedKeyMap = new Map();

        // set all
        this._supportedKeyMap.set(SupportedKeyboardKey.CTRL, false);

        this._viewer = viewer;
    }

    private addEventListeners() {
        this._element.addEventListener("pointerover", this.handlePointerOver.bind(this));
        this._element.addEventListener("pointerenter", this.handlePointerEnter.bind(this));
        this._element.addEventListener("pointerdown", this.handlePointerDown.bind(this));
        this._element.addEventListener("pointermove", this.handlePointerMove.bind(this));
        this._element.addEventListener("pointerup", this.handlePointerUp.bind(this));
        this._element.addEventListener("pointercancel", this.handlePointerCancel.bind(this));
        this._element.addEventListener("pointerout", this.handlePointerOut.bind(this));
        this._element.addEventListener("pointerleave", this.handlePointerLeave.bind(this));
        this._element.addEventListener("gotpointercapture", this.handleGotPointerCapture.bind(this));
        this._element.addEventListener("lostpointercapture", this.handleLostPointerCapture.bind(this));
        this._element.addEventListener("wheel", this.handleWheelEvent.bind(this));
        document.addEventListener('keyup', this.handleKeyUp.bind(this));
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
        this._element.addEventListener('contextmenu', (e) => { e.preventDefault(); });
        this._element.addEventListener('touchstart', (e) => { e.preventDefault(); });
        this._element.addEventListener('touchend', (e) => { e.preventDefault(); });
        this._element.addEventListener('touchmove', (e) => { e.preventDefault(); });
    }

    public getInputHandler() {
        return this._inputHandlers
    }

    public registerHandler(uniqueKey: string, handler: IInputEventHandler) {
        this._inputHandlers.set(uniqueKey, handler);
    }

    public unregisterHandler(uniqueKey: string) {
        this._inputHandlers.delete(uniqueKey);
    }

    private onPointerDown(pointerInfo: IPointerInfo) {
        pointerInfo.pointerUpAnalyzer && clearTimeout(pointerInfo.pointerUpAnalyzer);
        pointerInfo.isTimerNeeded = false;
        pointerInfo.isPointerDown = true;
        switch (pointerInfo.pointerEvent.button) { //down
            case InputPointerType.PRIMARY:
            case InputPointerType.SECONDARY:
            case InputPointerType.AUXILARY:
                {
                    this._inputHandlers.forEach(handler => {
                        handler.handlePointerEvent({ pointerType: pointerInfo.pointerEvent.button, eventType: InputEventType.POINTER_DOWN }, pointerInfo.pointerEvent)
                    }
                    );
                }
                break;
        }
    };


    public handlePointerDown(ev: PointerEvent): void {
        ev.preventDefault();
        this.isPointerMoved = false;
        this.isPointerDragged = false;
        let pointerInfo = this.pointerMap.get(ev.pointerId);
        if (!pointerInfo) {
            if (ev.pointerType === "touch") {
                const prevPointerInfo = this.pointerMap.get(ev.pointerId - 1);
                if (prevPointerInfo && prevPointerInfo.isPointerUp && prevPointerInfo.pointerEvent.pointerType === "touch") {
                    // pointerInfo = prevPointerInfo;
                    // this.inputHandler.handlePointerEvent(OrizuruViewerInputEvent.DOUBLE_TAP, pointerInfo.pointerEvent, inputInfo);
                    this.pointerMap.delete(ev.pointerId - 1);
                    this._inputHandlers.forEach(handler =>
                        handler.handlePointerEvent({ pointerType: pointerInfo.pointerEvent.button, eventType: InputEventType.DOUBLE_TAP }, pointerInfo.pointerEvent)
                    );
                    // this.inputHandler.handlePointerEvent(InputEventType.DOUBLE_TAP, ev, inputInfo);
                }
            }
        }
        if (pointerInfo) {
            pointerInfo.isPointerUp = false;
            pointerInfo.pointerdownCount += 1;
        }
        else {
            if (ev.pointerType === "touch") {
                // this.touchPointerId.add(ev.pointerId);
            }
            pointerInfo = {
                pointerdownPoint: new ScreenPoint(ev.pageX, ev.pageY),
                pointermovePoint: new ScreenPoint(ev.pageX, ev.pageY),
                pointerdownCount: 1,
                isDoubleClicked: false,
                isPointerUp: false,
                isPointerDown: false,
                isTimerNeeded: false,
                pointerEvent: ev
            };
            this.pointerMap.set(ev.pointerId, pointerInfo);
        }
        if (this.pointerMap.size > 1) { //pinch
            this.pinchCenter = new ScreenPoint();
            this.pointerMap.forEach((pInfo) => {
                this.pinchCenter.add(pInfo.pointerdownPoint);
                this.pinchCenter.x += pInfo.pointerdownPoint.x;
                this.pinchCenter.y += pInfo.pointerdownPoint.y;
            });

            // this.pinchCenter.x *= (1 / this.pointerMap.size);
            // this.pinchCenter.y *= (1 / this.pointerMap.size);
            this.pinchCenter.multiplyScalar(1 / this.pointerMap.size);
            this.pinchRadius = this.pinchCenter.distanceTo(pointerInfo.pointerdownPoint);

        }
        else {
            if (pointerInfo.pointerdownCount > 1) {
                pointerInfo.isDoubleClicked = true;
                pointerInfo.pointerDownAnalyzer && clearTimeout(pointerInfo.pointerDownAnalyzer);
            }
            else {
                pointerInfo.isDoubleClicked = false;
                pointerInfo.isTimerNeeded = true;

                pointerInfo.pointerDownAnalyzer && clearTimeout(pointerInfo.pointerDownAnalyzer);
                pointerInfo.pointerDownAnalyzer = setTimeout((pInfo: IPointerInfo, target: EventTarget) => {
                    if (!pInfo.isPointerUp) {
                        const obj: any = pInfo.pointerEvent;
                        obj["container"] = target;
                        this.onPointerDown(pInfo);
                    }
                }, doubleClickTimeDuration, pointerInfo, pointerInfo.pointerEvent.currentTarget);
            }
        }
    }

    public handlePointerMove(ev: PointerEvent): void {
        ev.preventDefault();
        const pointerInfo = this.pointerMap.get(ev.pointerId);
        if (pointerInfo) {
            if (this.pointerMap.size > 1) { //pinch
                pointerInfo.pointerdownPoint.set(ev.pageX, ev.pageY);
                const centre = new Vector2();
                this.pointerMap.forEach((pInfo) => {
                    centre.add(new Vector2(pInfo.pointerdownPoint.x, pInfo.pointerdownPoint.y));
                });
                centre.multiplyScalar(1 / this.pointerMap.size);
                const pinchRadius = centre.distanceTo(new Vector2(pointerInfo.pointerdownPoint.x, pointerInfo.pointerdownPoint.y));
                const swipeVal = this.pinchCenter.distanceTo(centre);
                const pinchVal = this.pinchRadius - pinchRadius;
                this.pinchRadius = pinchRadius;

                // console.log("debug", this._viewer.getProjectionMode());

                // if (swipeVal > this._viewer.testUIVal[3]) {
                if (swipeVal > (this._viewer.getProjectionMode() === "orthographic" ? 1.5 : 3)) {
                    this._inputHandlers.forEach(handler =>
                        handler.handleMultiplePointerEvent(InputMultiPointerEvent.SWIPE, { swipeFrom: this.pinchCenter, swipeTo: centre })
                    );
                    this.pinchCenter.copy(centre);
                } else if (Math.abs(pinchVal) > (this._viewer.getProjectionMode() === "orthographic" ? 0.5 : 0.7)) {
                    // } else if (Math.abs(pinchVal) > this._viewer.testUIVal[4]) {
                    this._inputHandlers.forEach(handler =>
                        handler.handleMultiplePointerEvent(InputMultiPointerEvent.PINCH, { center: this.pinchCenter, pinchOffset: pinchVal })
                    );
                }
            }
            else {

                if (pointerInfo.pointerdownCount > 0) {//dragged
                    pointerInfo.pointermovePoint.set(ev.pageX, ev.pageY);
                    const tolerance = pointerInfo.pointermovePoint.sub(pointerInfo.pointerdownPoint).length();
                    if (tolerance < clickTolerance && !this.isPointerMoved) { //still pointer down //pointer can come back to original position 
                        this.isPointerMoved = false; //tolerance not reached 
                    }
                    else {
                        //detect single click
                        pointerInfo.pointerUpAnalyzer && clearTimeout(pointerInfo.pointerUpAnalyzer);
                        pointerInfo.pointerDownAnalyzer && clearTimeout(pointerInfo.pointerDownAnalyzer);
                        pointerInfo.isTimerNeeded = false;
                        if (pointerInfo.isPointerUp) {
                            switch (pointerInfo.pointerEvent.button) { //down
                                case InputPointerType.PRIMARY:
                                case InputPointerType.SECONDARY:
                                case InputPointerType.AUXILARY:
                                    this._inputHandlers.forEach(handler =>
                                        handler.handlePointerEvent({ eventType: InputEventType.CLICK, pointerType: pointerInfo.pointerEvent.button }, pointerInfo.pointerEvent)
                                    )
                                    break;
                            }
                            this.pointerMap.delete(ev.pointerId);
                            this.isPointerMoved = true;
                            this._inputHandlers.forEach(handler =>
                                handler.handlePointerEvent({ eventType: InputEventType.POINTER_HOVER, pointerType: InputPointerType.NONE }, ev)
                            )
                        }
                        else {
                            if (!this.isPointerMoved) {
                                this.isPointerDragged = true;
                                switch (pointerInfo.pointerEvent.button) { //down
                                    case InputPointerType.PRIMARY:
                                    case InputPointerType.SECONDARY:
                                    case InputPointerType.AUXILARY:
                                        this._inputHandlers.forEach(handler =>
                                            handler.handlePointerEvent({ eventType: InputEventType.START_DRAG, pointerType: pointerInfo.pointerEvent.button }, pointerInfo.pointerEvent)
                                        )
                                }
                            }
                            this.isPointerMoved = true; //dragged
                            switch (pointerInfo.pointerEvent.button) { //down
                                case InputPointerType.PRIMARY:
                                case InputPointerType.SECONDARY:
                                case InputPointerType.AUXILARY:
                                    this._inputHandlers.forEach(handler =>
                                        handler.handlePointerEvent({ eventType: InputEventType.DRAGGED, pointerType: pointerInfo.pointerEvent.button }, ev)
                                    )
                                    break;
                            }

                        }

                        if (pointerInfo.isPointerDown) {
                            switch (pointerInfo.pointerEvent.button) { //down
                                case InputPointerType.PRIMARY:
                                case InputPointerType.SECONDARY:
                                case InputPointerType.AUXILARY:
                                    this._inputHandlers.forEach(handler =>
                                        handler.handlePointerEvent({ eventType: InputEventType.POINTER_MOVE, pointerType: pointerInfo.pointerEvent.button }, ev)
                                    )
                                    break;
                            }
                        }
                    }
                }
                else { //hover
                    this.isPointerMoved = true;
                    this._inputHandlers.forEach(handler =>
                        handler.handlePointerEvent({ eventType: InputEventType.POINTER_HOVER, pointerType: InputPointerType.NONE }, ev)
                    )
                }
            }
        }
        else { //hover
            this.isPointerMoved = true;
            this._inputHandlers.forEach(handler =>
                handler.handlePointerEvent({ eventType: InputEventType.POINTER_HOVER, pointerType: InputPointerType.NONE }, ev)
            )
        }
    }

    public handlePointerUp(ev: PointerEvent): void {
        ev.preventDefault();
        let pointerInfo = this.pointerMap.get(ev.pointerId);
        // if (ev.pointerType === "touch") {
        //     const prevPointerInfo = this.pointerMap.get(ev.pointerId - 1);
        //     if (prevPointerInfo && prevPointerInfo.isPointerUp && prevPointerInfo.pointerEvent.pointerType === "touch") {
        //         pointerInfo = prevPointerInfo;
        //     }
        // }

        if (pointerInfo) {
            pointerInfo.isPointerUp = true;
            if (pointerInfo.isTimerNeeded) {
                if (!pointerInfo.pointerUpAnalyzer) {
                    pointerInfo.pointerUpAnalyzer = setTimeout((pInfo: IPointerInfo) => {
                        if (pointerInfo) {
                            switch (pInfo.pointerEvent.button) { //down
                                case InputPointerType.PRIMARY:
                                case InputPointerType.SECONDARY:
                                case InputPointerType.AUXILARY:
                                    if (pointerInfo.isDoubleClicked) {
                                        //double click
                                        this._inputHandlers.forEach(handler =>
                                            handler.handlePointerEvent({ eventType: InputEventType.DOUBLE_CLICK, pointerType: pointerInfo.pointerEvent.button }, pointerInfo.pointerEvent)
                                        )
                                    }
                                    else {
                                        //single click
                                        this._inputHandlers.forEach(handler =>
                                            handler.handlePointerEvent({ eventType: InputEventType.CLICK, pointerType: pointerInfo.pointerEvent.button }, pointerInfo.pointerEvent)
                                        )
                                    }
                                    break;
                            }
                            pointerInfo.pointerDownAnalyzer && clearTimeout(pointerInfo.pointerDownAnalyzer);
                            this.pointerMap.delete(ev.pointerId);
                            pointerInfo.pointerUpAnalyzer = undefined;
                        }
                    }, doubleClickTimeDuration, pointerInfo);
                }
            }
            else { //pointer up occured
                if (this.isPointerDragged) {
                    this.isPointerDragged = false;
                    switch (pointerInfo.pointerEvent.button) { //down
                        case InputPointerType.PRIMARY:
                        case InputPointerType.SECONDARY:
                        case InputPointerType.AUXILARY:
                            this._inputHandlers.forEach(handler =>
                                handler.handlePointerEvent({ eventType: InputEventType.END_DRAG, pointerType: pointerInfo.pointerEvent.button }, ev)
                            )
                            break;
                    }
                }
                if (pointerInfo.isPointerDown) {
                    switch (pointerInfo.pointerEvent.button) { //down
                        case InputPointerType.PRIMARY:
                        case InputPointerType.SECONDARY:
                        case InputPointerType.AUXILARY:
                            this._inputHandlers.forEach(handler =>
                                handler.handlePointerEvent({ eventType: InputEventType.POINTER_UP, pointerType: pointerInfo.pointerEvent.button }, ev)
                            )
                            break;
                    }
                }

                if (pointerInfo.isPointerDown && !this.isPointerMoved) {
                    switch (pointerInfo.pointerEvent.button) { //down
                        case InputPointerType.PRIMARY:
                        case InputPointerType.SECONDARY:
                        case InputPointerType.AUXILARY:
                            this._inputHandlers.forEach(handler =>
                                handler.handlePointerEvent({ eventType: InputEventType.LONG_CLICK, pointerType: pointerInfo.pointerEvent.button }, pointerInfo.pointerEvent)
                            )
                            break;
                    }
                }
                pointerInfo.isPointerDown = false;
                this.pointerMap.delete(ev.pointerId);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public handlePointerEnter(ev: PointerEvent): void {
        ev.preventDefault();
    }

    public handlePointerLeave(ev: PointerEvent): void {
        ev.preventDefault();
        this._inputHandlers.forEach(handler =>
            handler.handlePointerEvent({ eventType: InputEventType.POINTER_LEAVE, pointerType: InputPointerType.NONE }, ev)
        )
        if (ev.pointerType !== "touch") {
            this.pointerMap.delete(ev.pointerId);
        }
    }

    public handlePointerCancel(ev: PointerEvent): void {
        ev.preventDefault();
        this.pointerMap.delete(ev.pointerId);
    }

    public handlePointerOut(ev: PointerEvent): void {
        ev.preventDefault();
        this._inputHandlers.forEach(handler =>
            handler.handlePointerEvent({ eventType: InputEventType.POINTER_OUT, pointerType: InputPointerType.NONE }, ev)
        )
        if (ev.pointerType !== "touch") {
            this.pointerMap.delete(ev.pointerId);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public handlePointerOver(ev: PointerEvent): void {
        ev.preventDefault();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public handleGotPointerCapture(ev: PointerEvent): void {
        ev.preventDefault();
    }

    public handleLostPointerCapture(ev: PointerEvent): void {
        ev.preventDefault();
        // this.pointerMap.delete(ev.pointerId);
    }

    public handleWheelEvent(ev: WheelEvent): void {
        ev.preventDefault();
        this._controlManager.handleWheelEvent(ev);
    }

    public handleKeyUp(ev: KeyboardEvent): void {
        // ev.preventDefault();
        const key = ev.key.toLowerCase() as SupportedKeyboardKey;
        this._supportedKeyMap.set(key, false);
    }

    public handleKeyDown(ev: KeyboardEvent): void {
        // ev.preventDefault();
        const key = ev.key.toLowerCase() as SupportedKeyboardKey;
        this._supportedKeyMap.set(key, true);
    }
}
